import { useState, useEffect } from 'react';
import useSWRInfinite from 'swr/infinite';

import { format } from 'date-fns';
import api from 'src/store/api';

import {
    IGetCommonStatData,
    IGetCommonStatDataFormatted,
    IGetCommonStatSummaryDataAttributes,
    IResponse,
} from '@api/types';
import { ICommonFilterValues, IDayStat, IOrderOption } from '@pages/statistics/common/interfaces';
import { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import { dayStatConfig, LIMIT } from './config';
import {
    formatCommonStat,
    convertFilterIntoParams,
    reducePlacementStat,
    FiltersConvertedToParams,
} from './utils';
import { CurrencyTypes } from '../../../../types';

export function useDayStat(
    currency: 'USD' | 'RUB' | 'TON' | 'EUR' | 'GBP',
): {
    cards: IDayStat[];
    isFetching: boolean;
    error: string;
} {
    const [cards, setCards] = useState<IDayStat[]>([]);
    const [error, setError] = useState<string>('');
    const [isFetching, setIsFetching] = useState<boolean>(true);
    const today: string = format(new Date(), 'yyyy-MM-dd');

    const params = {
        type: 'day',
        currency,
        dateFrom: today,
        dateTo: today,
    };

    useEffect((): void => {
        setError('');
        setIsFetching(true);
        api.stats
            .getStatistic({ params })
            .then((data: IResponse<IGetCommonStatData>): void => {
                const formatted: IGetCommonStatSummaryDataAttributes = data.data?.[0].attributes;

                const cardsArray: IDayStat[] = dayStatConfig.map(
                    (item: Omit<IDayStat, 'value'>): IDayStat => {
                        const { field, isCurrency = false } = item;
                        const resValue = String(formatted?.[field] || 0);

                        const values = {
                            USD: `$ ${resValue}`,
                            RUB: `${resValue} ₽`,
                            EUR: `€ ${resValue}`,
                            GBP: `£ ${resValue}`,
                            TON: `${resValue} TON`,
                        };

                        const value: string = isCurrency ? values[currency] : resValue;

                        return {
                            ...item,
                            value,
                        };
                    },
                );

                setCards(cardsArray);
                setIsFetching(false);
            })
            .catch(err => {
                const requestId = err.response?.headers?.get('request-id');
                const errorMessage = requestId ? `request-id: ${requestId}` : String(err);
                setError(errorMessage);
                setIsFetching(false);
            });
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [currency]);

    return { cards, error, isFetching };
}

export function useCommonStat({
    sort,
    filters,
    isForChart,
}: {
    sort?: IOrderOption;
    filters: ICommonFilterValues;
    isForChart?: boolean;
}): {
    data: IGetCommonStatDataFormatted[];
    nextPage: () => void;
    isReachingEnd: boolean;
    error: { message: string };
    isLoading: boolean;
} {
    const getKey = (
        index: number,
        previousPageData: IGetCommonStatDataFormatted[] | null,
    ): (number | string)[] | null => {
        if (previousPageData && !previousPageData.length) return null;

        const offset: number = LIMIT * index;

        const filtersDependencies: string[] = Object.values(filters).map(value =>
            Array.isArray(value) ? value.toString() : value,
        );

        return isForChart
            ? ['chart-stat', ...filtersDependencies]
            : [`common-stat-${offset}`, offset, ...Object.values(sort), ...filtersDependencies];
    };
    const { data, size, error, setSize, ...other } = useSWRInfinite(
        getKey,
        async (_, offset: number): Promise<IGetCommonStatDataFormatted[]> => {
            const convertedFilters: FiltersConvertedToParams = convertFilterIntoParams(filters);

            // Данные для графика не используют пагинацию и сортировку
            const params:
                | (FiltersConvertedToParams & {
                      orderDirection: string;
                      currency?: CurrencyTypes;
                      placement?: null;
                  })
                | (FiltersConvertedToParams & {
                      limit: number;
                      offset: number;
                      orderDirection: 'desc' | 'asc';
                      currency?: CurrencyTypes;
                      placement?: null;
                      order: string;
                  }) = isForChart
                ? { ...convertedFilters, orderDirection: 'asc' }
                : { ...convertedFilters, ...sort, offset, limit: LIMIT };

            const { data: stat } = (await api.stats.getStatistic({
                params,
            })) as ICreateUseRequestInfiniteResponse<IGetCommonStatData[]>;

            const formattedStat = formatCommonStat(stat);

            if (filters.placementIds && !filters.offerIds) {
                // одна площадка - один оффер поэтому схлопываем строки для одинаковых площадок
                return reducePlacementStat(formattedStat, filters);
            }
            return formattedStat;
        },
        {
            revalidateOnFocus: false,
            dedupingInterval: 10000,
            persistSize: true,
            shouldRetryOnError: false,
            revalidateFirstPage: false,
        },
    );

    // todo: WHY!?
    const concatedData = data ? [].concat(...data) : [];

    const isLoadingInitialData: boolean = !data && !error;
    // todo naming!
    const isWTFLoading: boolean = size > 0 && data && typeof data[size - 1] === 'undefined';
    const isLoading: boolean = isLoadingInitialData || isWTFLoading;
    const isEmpty: boolean = data?.[0]?.length === 0;
    const isReachingEnd: boolean = isEmpty || (data && data[data.length - 1]?.length < LIMIT);

    return {
        data: concatedData,
        nextPage: () => setSize(size + 1),
        isReachingEnd,
        error,
        isLoading,
        ...other,
    };
}

function createBaitNode(): HTMLAnchorElement {
    const node = document.createElement('a');
    node.innerHTML = '&nbsp';
    node.setAttribute('href', '/open?refer');

    document.body.appendChild(node);

    return node;
}

export function useUBlockDetection(): boolean {
    const [hasUblock, setHasUblock] = useState<boolean>(false);

    useEffect((): void => {
        const node = createBaitNode();

        if (!node || node.offsetHeight === 0) {
            setHasUblock(true);
        }

        document.body.removeChild(node);
    }, []);

    return hasUblock;
}
